import * as React from "react";

import { Field, FieldInputProps, FieldProps, useFormikContext } from "formik";
import { useButtonTexts, useComponentTexts } from "Store/texts/hooks";

import { Checkbox as StoreblocksCheckbox } from "@storeblocks/checkbox";
import { StyledAlert } from "App/global-style";
import { AlertType } from "Components/Form/Alert";
import styled from "styled-components";
import { checkboxTrackId } from "Utils/analytics/analyticsUtils";

export interface CheckboxProps {
  id: string;
  label: string;
  name: string;
  paragraph?: string;
  hint?: string;
  error?: string;
  errorTitle?: string;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement>,
    field: FieldInputProps<any>,
  ) => void;
  maxLinesHint?: number;
}

/**
 * Renders a checkbox based on @storeblocks/checkbox.
 * Current issues:
 * TODO: Renders no actual input of type checkbox.
 */
export const Checkbox = ({
  id,
  label,
  name,
  paragraph,
  hint,
  error,
  errorTitle,
  onChange,
  maxLinesHint = 6,
}: CheckboxProps): React.ReactElement => {
  const [{ alert }, lang] = useComponentTexts();
  const [{ readMore, readLess }] = useButtonTexts();
  const formikContext = useFormikContext<Record<string, unknown>>();
  return (
    <Field name={name} type="checkbox">
      {({ field }: FieldProps) => (
        <>
          <StoreblocksCheckbox
            {...field}
            onChange={(e) => {
              formikContext.setFieldValue(id, e.target.checked);
              formikContext.validateForm({
                ...formikContext.values,
                [id]: e.target.checked,
              });
              onChange && onChange(e, field);
            }}
            id={`${id}_parent`}
            name={`${name}_parent`}
            value={Boolean(field.checked)}
            options={[
              {
                id,
                label,
                hint,
                hintProps: {
                  maxLines: maxLinesHint,
                  readLessLabel: `${readLess[lang]}`,
                  readMoreLabel: `${readMore[lang]}`,
                },
                value: id,
              },
            ]}
            data-trackid={checkboxTrackId(id, field.checked ?? false)}
          />
          {paragraph && <CheckboxParagraph>{paragraph}</CheckboxParagraph>}
          {error && (
            <StyledAlert
              type="error"
              alertId={id}
              alertType={AlertType.STOPPER_ALERT}
              title={errorTitle ?? alert.defaultAlertTitle[lang]}
              description={error}
              fluid
              marginTop="16px"
            />
          )}
        </>
      )}
    </Field>
  );
};

export const BoldCheckbox = styled(Checkbox)`
  margin-top: 2em;
  padding-top: 2em;

  & span {
    font-weight: 600;
  }
`;

const CheckboxParagraph = styled.p`
  margin-bottom: 1em;
  padding-bottom: 1em;
  padding-left: 32px;
`;
